import { useCallback } from 'react';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  DeleteTimeSignalsMutation,
  TimeSignalClassification,
  TimeSignalsDocument,
  useDeleteTimeSignalsMutation
} from 'graphql/types';
import { useMeContext } from 'modules/me/MeContextProvider';

import {
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { TIMELINE_TIMESIGNAL_COUNT } from '../Timeline';
import { clearCachedDates } from '../cache/utils/time-signal-utils';
import {
  useTimeSignalGetParam,
  TimeSignalGetParam
} from './useTimeSignalParam';

export const deleteTimeSignalsCache = (param: TimeSignalGetParam) => (
  cache: ApolloCache<DeleteTimeSignalsMutation>,
  mutationResult: FetchResult<
    DeleteTimeSignalsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.deleteTimeSignals?.ids) {
    cache.writeQuery({
      query: TimeSignalsDocument,
      variables: param,
      data: {
        timeSignals: {
          __typename: 'TimeSignalsResult',
          items: [],
          totalCount: 0,
          cursor: null
        }
      }
    });
  }
};

export const useDeleteAllTimeSignals = (
  classificationType: TimeSignalClassification
): { onTimeSignalsDelete: () => Promise<void> } => {
  const [bulkDeleteTimeSignals] = useDeleteTimeSignalsMutation();

  const { id } = useMeContext() || {};
  const {
    filterContext: { pageType }
  } = useNotesFilterContext();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );

  const onTimeSignalsDelete = useCallback(async () => {
    let result: any = null;
    clearCachedDates(id);
    do {
      result = await bulkDeleteTimeSignals({
        variables: {
          input: {
            selectionFilter: {
              classifications: [classificationType]
            },
            cursor: result?.data?.deleteTimeSignals.cursor ?? null,
            limit: 100
          }
        },
        awaitRefetchQueries: true,
        update: deleteTimeSignalsCache(param),
        refetchQueries: ['timeSignalsCount']
      });
    } while (result?.data?.deleteTimeSignals.cursor);
  }, [bulkDeleteTimeSignals, classificationType, id, param]);
  return { onTimeSignalsDelete };
};
